/** Fixed locale so list timestamps match across modules regardless of browser region (e.g. en-IN vs en-US). */
const APP_DATETIME_LOCALE = 'en-US';

const APP_DATETIME_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
};

const API_DATETIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?(?:([zZ]|[+-]\d{2}:\d{2}))?$/;

type AppTimestampInput = string | number | number[] | null | undefined;

/**
 * Parse backend timestamps (java.time.LocalDateTime, often without offset on UTC servers).
 * Naive values are treated as UTC so all pages show the same instant in the user's timezone.
 */
export function parseApiDateTime(value: AppTimestampInput): Date | null {
  if (value == null) return null;
  try {
    if (Array.isArray(value)) {
      const [y, m, d, h = 0, min = 0, sec = 0, nano = 0] = value;
      const millis =
        typeof nano === 'number' && nano > 999 ? Math.floor(nano / 1_000_000) : Number(nano) || 0;
      const date = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1, h, min, sec, millis));
      return Number.isNaN(date.getTime()) ? null : date;
    }
    if (typeof value === 'number') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const raw = String(value).trim();
    if (!raw) return null;
    const match = raw.match(API_DATETIME_RE);
    if (!match) {
      const fallback = new Date(raw);
      return Number.isNaN(fallback.getTime()) ? null : fallback;
    }
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    const hour = Number(match[4]);
    const minute = Number(match[5]);
    const second = Number(match[6]);
    const millis = Number((match[7] || '').padEnd(3, '0').slice(0, 3) || '0');
    const zone = match[8];
    if (!zone || zone.toUpperCase() === 'Z') {
      return new Date(Date.UTC(year, month, day, hour, minute, second, millis));
    }
    const sign = zone.startsWith('-') ? -1 : 1;
    const zoneHour = Number(zone.slice(1, 3));
    const zoneMinute = Number(zone.slice(4, 6));
    const offsetMs = sign * ((zoneHour * 60 + zoneMinute) * 60 * 1000);
    return new Date(Date.UTC(year, month, day, hour, minute, second, millis) - offsetMs);
  } catch {
    return null;
  }
}

/**
 * Format an ISO or date string for consistent "Last Executed" display.
 * Uses the same locale/options as {@link formatAppTimestamp} so tables and detail views align.
 */
export function formatLastExecutedTimestamp(isoOrDateString: string | null | undefined): string {
  if (isoOrDateString == null || String(isoOrDateString).trim() === '') return '—';
  const date = parseApiDateTime(isoOrDateString);
  if (!date) return '—';
  return date.toLocaleString(APP_DATETIME_LOCALE, APP_DATETIME_FORMAT);
}

/**
 * Uniform timestamp formatter used by list views across modules.
 * Keeps date+time representation consistent everywhere.
 */
export function formatAppTimestamp(value: AppTimestampInput, fallback = 'N/A'): string {
  const date = parseApiDateTime(value);
  if (!date) return fallback;
  return date.toLocaleString(APP_DATETIME_LOCALE, APP_DATETIME_FORMAT);
}
